import moment from 'moment-timezone';
import { enGB, ja, fr, de, nl, es, pt, ar, it, th, zhCN, ko, vi, id, faIR } from 'date-fns/locale';
import { ValueLabel } from '../models';
import 'moment/locale/es';
import 'moment/locale/ja';
import 'moment/locale/fr';
import 'moment/locale/de';
import 'moment/locale/nl';
import 'moment/locale/pt';
import 'moment/locale/ar-dz';
import 'moment/locale/it';
import 'moment/locale/zh-cn';
import 'moment/locale/th';
import 'moment/locale/ko';
import 'moment/locale/vi';
import 'moment/locale/id';
import 'moment/locale/fa';

moment.locale('en');
const CY_TIMEZONE = 'Europe/Nicosia';

export function a11yProps(index: number | string): any {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

export function dateObjectToMonthYearString(value: Date | null): string | null {
  if (value != null) return moment.utc(`${value.getUTCFullYear().toString()}-${(value.getMonth() + 1).toString()}-${value.getDate().toString()}`, 'YYYY-MM-DD').format('MMMM YYYY');

  return null;
}

export const errorInitFunction = (): string | undefined => {
  throw new Error('AppStateContext not initialized');
};

export const formatNumber = (v: number, currency: string): string => Number(v).toLocaleString('en-US', { style: 'currency', currency });

export const formatNumberForChart = (v: number, currency: string): string => Number(v).toLocaleString('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function convertUTCDateToLocal(utcDateTime: string, format?: string): string {
  return moment.utc(utcDateTime).local().format(format);
}

export function convertLocalDateToUTC(localDateTime: string, format?: string): string {
  return moment.utc(localDateTime).format(format);
}

export function convertDate(Datetime: string): string {
  return moment(Datetime).format('DD/MM/YYYY');
}

export const formatTimeToCY = (value: string | Date | null | undefined, outputFormat = 'DD/MM/YYYY HH:mm:ss', inputFormat?: string): string => {
  if (!value) return '-';
  const date = inputFormat ? moment.utc(value, inputFormat) : moment.utc(value);
  return date.isValid() ? date.tz(CY_TIMEZONE).format(outputFormat) : '-';
};

export const getLocale = (language: string): Locale => {
  switch (language) {
    case 'en':
      return enGB;
    case 'ja':
      return ja;
    case 'fr':
      return fr;
    case 'de':
      return de;
    case 'nl':
      return nl;
    case 'es':
      return es;
    case 'pt':
      return pt;
    case 'ar':
      return ar;
    case 'it':
      return it;
    case 'zh':
      return zhCN;
    case 'th':
      return th;
    case 'fa':
      return faIR;
    case 'ko':
      return ko;
    case 'vi':
      return vi;
    case 'id':
      return id;
    default:
      return enGB;
  }
};

export const getSelectedColumnsInEn = (enColumns: string[], selectedColumns: string[], columns: string[]): string[] => {
  const result: string[] = [];

  columns.forEach((column, index) => {
    if (selectedColumns.includes(column)) {
      result.push(enColumns[index]);
    }
  });

  return result;
};

export const getTranslatedColumnsInCorrectOrder = (selectedColumns: string[], columns: string[]): string[] => {
  const result: string[] = [];

  columns.forEach((column) => {
    if (selectedColumns.includes(column)) {
      result.push(column);
    }
  });

  return result;
};

export const getTranslation = (translation: Record<string, string>, value: string) =>
  translation[
    value
      .toLowerCase()
      .replaceAll(' ', '_')
      .replaceAll('__', '_')
      .replaceAll(/[^a-zA-Z0-9_]/g, '')
  ] || value;

export const getOptions = (list: any[], labelName: string, valueName: string): ValueLabel[] =>
  list.map((elem) => ({
    value: elem[valueName],
    label: elem[labelName],
  }));

export const concatErrorMessages = (translation: any, errorObj: any): string => {
  const messages: string[] = [];

  if (errorObj.errors) {
    Object.values(errorObj.errors).forEach((errorArray) => {
      if (Array.isArray(errorArray)) {
        errorArray.forEach((error) => {
          if (error.message) {
            const translated = getTranslation(translation, error.message);
            messages.push(translated);
          }
        });
      }
    });
  }

  return messages.join(', ');
};

export const formatDate = (value: Date | null, locale: string | undefined) => {
  let lang = 'en';
  if (locale) {
    if (locale === 'ar') {
      lang = 'ar-dz';
    } else if (locale === 'zh') {
      lang = 'zh-cn';
    } else if (locale === 'fa') {
      lang = 'fa';
    } else {
      lang = locale;
    }
  }
  const date = moment(value);
  date.locale(lang);
  return date.format('DD MMM YYYY');
};
